import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useBasket } from './BasketContext';
import CheckoutForm from './CheckoutForm';
import OrderSummary from './OrderSummary';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutContainer = styled.div`
  max-width: 1100px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const CheckoutLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const FormContainer = styled.div`
  background-color: #ffffff;
  padding: 2rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const Checkout = () => {
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { items, shippingOption, discount } = useBasket();

  const subtotal = items.reduce((acc, item) => acc + item.price * item.quantity, 0);
  // Recomputed from the live subtotal for display; the backend re-validates the
  // code and computes the real, authoritative discount when it creates the
  // PaymentIntent below, so this can't be used to under-pay.
  const discountAmount = discount
    ? Math.min(discount.type === 'percent' ? subtotal * (discount.value / 100) : discount.value, subtotal)
    : 0;
  const shippingCost = shippingOption === 'post' && subtotal > 0 ? 2.99 : 0;
  const total = Math.max(0, subtotal - discountAmount) + shippingCost;

  useEffect(() => {
    // Reset state on total change
    setLoading(true);
    setError(null);
    setClientSecret("");

    if (total > 0) {
      // Create PaymentIntent as soon as the page loads
      fetch(`${process.env.REACT_APP_BACKEND_URL}/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shippingOption: shippingOption, // Inform the backend about the shipping choice
          // The backend looks up the real price for each id itself - it does not
          // trust a price or total from the browser. Only send IDs/names/quantities.
          items: items.map(item => ({
            id: item.id, n: item.name.substring(0, 20), q: item.quantity
          })),
          // The backend re-validates the code and recomputes the discount itself.
          discountCode: discount?.code,
        }),
      })
        .then(async (res) => {
          if (!res.ok) {
            // Try to parse error from backend, provide fallback
            const { error: errorMessage } = await res.json().catch(() => ({
              error: "Unknown server error. Check the backend console.",
            }));
            throw new Error(errorMessage || `Server responded with ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          if (data.clientSecret) {
            setClientSecret(data.clientSecret);
          } else {
            // This case handles if the server responds 200 OK but without a clientSecret
            throw new Error("Client secret not found in server response.");
          }
        })
        .catch((err) => {
          console.error('Error creating payment intent:', err);
          setError(err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // If there's no total, we're not loading anything.
      setLoading(false);
    }
  }, [total, shippingOption, items, discount]);

  const appearance = {
    theme: 'stripe',
  };
  const options = {
    clientSecret,
    appearance,
  };

  const renderContent = () => {
    if (loading) {
      return <p>Loading payment details...</p>;
    }
    if (error) {
      return <p style={{ color: 'red' }}>Error: {error}</p>;
    }
    if (total === 0) {
      return <p>Your basket is empty. Add items to proceed to checkout.</p>;
    }
    if (clientSecret) {
      return (
        <CheckoutLayout>
          <FormContainer>
            <Elements options={options} stripe={stripePromise}>
              <CheckoutForm total={total} clientSecret={clientSecret} />
            </Elements>
          </FormContainer>
          <OrderSummary
            items={items}
            subtotal={subtotal}
            discount={discount}
            discountAmount={discountAmount}
            shippingCost={shippingCost}
            total={total}
          />
        </CheckoutLayout>
      );
    }
    return <p>Something went wrong. Please try again.</p>;
  };

  return (
    <CheckoutContainer>
      <PageTitle>Secure Checkout</PageTitle>
      {renderContent()}
    </CheckoutContainer>
  );
};

export default Checkout;